import { BID_SOURCES, USAGE_EVENTS, usageRoutingOf } from '@/utils/usageEventKeys'
import { convertCurrencyStringToNumber } from '@/store/modules/campaign.store'
import { getConfiguredMinimumSeconds, toCents } from '@/store/modules/market-context.store'

/**
 * The save's usage event for the four pages around the budget step: create, edit, softphone create
 * and softphone edit (docs/MARKET_CONTEXT_USAGE_ANALYTICS.md). The create and edit requests carry
 * no `source`, so this event is the budget step's only record of where a bid came from.
 *
 * The bid and minimum are the Budget step's Bid Amount and Minimum Duration as saved, whatever the
 * routing type: a RingbaRTB bidder or a Programmatic API overrides them, so the dashboard reads
 * adoption on `routing: static`.
 */

function savedBidOf(campaign) {
  return convertCurrencyStringToNumber(campaign?.budget?.maxBidPerCallInUSD) || null
}

function compareBid(bidInUSD, suggestedBidInUSD) {
  if (suggestedBidInUSD == null) {
    return 'no_suggestion'
  }

  if (bidInUSD == null) {
    return 'no_bid'
  }

  const difference = toCents(bidInUSD) - toCents(suggestedBidInUSD)

  if (difference === 0) {
    return 'equal'
  }

  return difference > 0 ? 'above' : 'below'
}

function compareMinimum(minimumSeconds, suggestedMinimumSeconds) {
  if (suggestedMinimumSeconds == null) {
    return 'none_suggested'
  }

  return minimumSeconds === suggestedMinimumSeconds ? 'equal' : 'different'
}

// `suggestion` when the saved values are what the button set, `suggestion_edited` when the buyer
// changed them since, `manual` when no button was used. A button that sets the bid alone leaves the
// minimum to the buyer, so changing it afterwards keeps `suggestion`
function sourceOf(used, bidInUSD, minimumSeconds) {
  if (!used) {
    return BID_SOURCES.manual
  }

  const keptBid = bidInUSD != null && toCents(bidInUSD) === toCents(used.bidInUSD)
  const keptMinimum = used.minimumSeconds == null || minimumSeconds === used.minimumSeconds

  return keptBid && keptMinimum ? BID_SOURCES.suggestion : BID_SOURCES.suggestionEdited
}

/**
 * `market_budget_saved`'s context.
 *
 * @param {object} options
 * @param {object} options.usage the campaign store's `buyerCampaignMarketUsage`
 * @param {object} options.campaign the campaign as saved
 * @param {object|null} options.original the campaign as an edit page loaded it; null on a create
 *   page, where the bid is new and the minimum is new when one is on
 * @param {string} [options.page] the route's name
 * @param {string|null} [options.campaignId]
 */
export function getBudgetSavedContext({ usage, campaign, original = null, page, campaignId = null }) {
  const bidInUSD = savedBidOf(campaign)
  const minimumSeconds = getConfiguredMinimumSeconds(campaign)

  const bidChanged = original
    ? toCents(bidInUSD || 0) !== toCents(savedBidOf(original) || 0)
    : true

  const minimumChanged = original
    ? minimumSeconds !== getConfiguredMinimumSeconds(original)
    : minimumSeconds != null

  return {
    page,
    campaignId,
    routing: usageRoutingOf(campaign?.routing?.type),
    surface: usage.surface,
    bidChanged,
    minimumChanged,
    source: sourceOf(usage.used, bidInUSD, minimumSeconds),
    used: usage.used?.button || 'none',
    bidVsSuggested: compareBid(bidInUSD, usage.suggestedBidInUSD),
    minimumVsSuggested: compareMinimum(minimumSeconds, usage.suggestedMinimumSeconds),
    bidInUSD,
    suggestedBidInUSD: usage.suggestedBidInUSD,
    minimumSeconds,
    suggestedMinimumSeconds: usage.suggestedMinimumSeconds
  }
}

export default {
  methods: {
    /**
     * Sends `market_budget_saved` after a successful save, when the market card or panel rendered
     * with data on this visit; nothing otherwise. A save that changed neither the bid nor the
     * minimum, a new daily budget for example, is sent too, and the dashboard leaves it out of bid
     * changes. An edit page sends it before it reloads the campaign, which drops the record.
     *
     * @param {object} [options]
     * @param {object|null} [options.original] the campaign as the edit page loaded it; null on create
     * @param {string|null} [options.campaignId]
     */
    trackMarketBudgetSaved({ original = null, campaignId = null } = {}) {
      const { buyerCampaign, buyerCampaignMarketUsage } = this.$store.state.campaign

      if (!buyerCampaignMarketUsage) {
        return
      }

      // The campaign is saved: a failure here must not reach the page's error message
      try {
        this.$track(USAGE_EVENTS.marketBudgetSaved.name, getBudgetSavedContext({
          usage: buyerCampaignMarketUsage,
          campaign: buyerCampaign,
          original,
          page: this.$route?.name,
          campaignId
        }))
      } catch (error) {
        console.error('Usage analytics: could not send market_budget_saved', error)
      }

      // What the button set is saved now. A later save on this visit counts its own
      this.$store.commit('campaign/SET_BUYER_CAMPAIGN_MARKET_USE', null)
    }
  }
}
